"use client";

import { LocalLessonTimeHoldStep, calendarFirstRequest, formatLocalLessonTime, getCheckoutStartDeadline, type LocalLessonTimeHold } from "./LocalLessonTimeHoldStep";

import { normalizeLocalLessonEmail } from "@/lib/localLessons/customerEmail";

import { useEffect, useState } from "react";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import Image from "next/image";
import Link from "next/link";
import { createPortal } from "react-dom";
import {
  AppleLogo,
  ArrowRight,
  CalendarDots,
  ChatCircleDots,
  CheckCircle,
  Clock,
  FileText,
  GoogleLogo,
  LockKey,
  MapPin,
  MicrophoneStage,
  Play,
  ShieldCheck,
  StripeLogo,
  Target,
  TrendUp,
  UserFocus,
  UsersThree,
  X,
} from "@phosphor-icons/react";
import { useTrackEvent } from "@/hooks/useTrackEvent";
import { getCheckoutAnalyticsConsent } from "@/lib/analytics/checkoutAnalyticsConsent";
import { PurchaseReviewDialog } from "@/components/local-lessons/PurchaseReviewDialog";
import { CheckoutConsentStep } from "@/components/local-lessons/CheckoutConsentStep";
import { LocalLessonCalStep } from "@/components/local-lessons/LocalLessonCalStep";
import { VocalDiscoveryDetailsDialog } from "@/components/local-lessons/VocalDiscoveryDetailsDialog";
import { VocalDiscoveryPurchaseFlow } from "@/components/local-lessons/VocalDiscoveryPurchaseFlow";
import {
  LIUBA_LOCAL_LESSON_VENUES,
  formatLiubaVenueAddress,
  getLiubaVenueDisplayName,
  type LiubaLocalLessonVenueId,
} from "@/lib/localLessons/liubaVenues";
import {
  CHECKOUT_FLEXIBLE_PAYMENT_DISCLAIMER,
  FLEXIBLE_PAYMENT_DISCLAIMER,
  formatLocalLessonPrice,
  formatSessionDuration,
  formatTotalCoachingTime,
  getLocalLessonAnalyticsMetadata,
  getLocalLessonPriceDetail,
  getLocalLessonProduct,
  getPublicLocalLessonProducts,
  isLocalLessonProductAvailableAtVenue,
  type LocalLessonProductKey,
} from "@/lib/localLessons/localLessonProductCatalog";
import { getVideoTestimonial } from "@/lib/videoTestimonials";
import {
  BURY_MANUAL_ORDERING_COPY,
  LOCAL_LESSON_MANUAL_ORDERING_COPY,
} from "@/lib/localLessons/manualOrdering";
import {
  adaptLocalLessonCalendarResponse,
  isLocalBookingTimeExpiredCode,
  type LocalLessonCalendarAdapterState,
} from "@/lib/localLessons/localLessonCalendarAdapter";
import {
  EMPTY_CHECKOUT_CONSENT,
  validateCheckoutConsentInput,
  requireElyEarlyPerformanceRequest,
  type CheckoutConsentInput,
} from "@/lib/consent/bookingConsent";

type LocalLessonOption = {
  productKey: LocalLessonProductKey;
  title: string;
  price: string;
  priceDetail: string;
  badge: string;
  recommended?: boolean;
  summary: string;
  detail: string;
  reassurance: string;
  result: string;
  buttonLabel: string;
};

const LOCAL_LESSON_OPTION_COPY: Record<LocalLessonProductKey, Omit<LocalLessonOption, "productKey" | "title" | "price" | "priceDetail">> = {
  liuba_intro_lesson: {
    badge: "Best first lesson",
    recommended: true,
    summary: "Start here if you are new to Liuba, returning to singing, nervous, or unsure what support you need.",
    detail: "Liuba listens to your voice, your goals and what feels difficult, then follows up with an emailed voice report.",
    reassurance: "Most first-time students should start here before choosing a longer lesson route.",
    result: "You leave with a written record of where your voice is now and which lesson path makes sense.",
    buttonLabel: "Book",
  },
  liuba_single_lesson: {
    badge: "Best for experienced singers",
    summary: "Choose this if you already know you want one focused coaching hour with Liuba.",
    detail: "Bring one clear goal, song, technical issue or performance concern into a full private session.",
    reassurance: "Good for returning students or confident singers who already know what they want help with.",
    result: "You get direct coaching on the issue that matters most right now.",
    buttonLabel: "Book",
  },
  liuba_lesson_pack_3: {
    badge: "Three-session package",
    summary: "Three one-hour in-person coaching sessions with flexible booking.",
    detail: "Use three separate sessions to work on technique, confidence or repertoire with continuity between lessons.",
    reassurance: "Available at Ely and Bury St Edmunds, with each lesson time arranged manually after payment.",
    result: "You get a focused three-session coaching plan without booking one long appointment.",
    buttonLabel: "Book",
  },
  liuba_lesson_pack_4: {
    badge: "Historical four-session package",
    summary: "Retained only for historical purchase, recovery and fulfilment support.",
    detail: "This package is no longer offered on current public sales surfaces.",
    reassurance: "Existing purchasers keep their original four-session entitlement.",
    result: "Historical orders remain recognisable and manageable.",
    buttonLabel: "Book",
  },
  liuba_lesson_pack_6: {
    badge: "Six-session package",
    summary: "Six separate one-hour sessions for students who already know they want a fuller run of lessons.",
    detail: "Plan a longer coaching block when you want a sustained rhythm and a clearer development arc.",
    reassurance: "Secure the package online, then arrange each exact lesson time directly with Singing Attitude.",
    result: "You create space for deeper technical and confidence work over time.",
    buttonLabel: "Book",
  },
  liuba_lesson_pack_10: {
    badge: "Ten-session package",
    summary: "Ten separate one-hour sessions for sustained in-person coaching and long-term progress.",
    detail: "Use a ten-session coaching block to develop technique, confidence and repertoire with continuity.",
    reassurance: "Available at Ely and Bury St Edmunds, with each exact lesson time confirmed separately.",
    result: "You create a longer, structured coaching rhythm with space for measurable progress.",
    buttonLabel: "Book",
  },
  liuba_half_hour_pack_6: {
    badge: "Six shorter sessions · Ely only",
    summary: "Six shorter 30-minute private sessions for Ely students who want lighter, more focused in-person work.",
    detail: "Choose shorter sessions if a lighter rhythm feels more manageable than full-hour coaching.",
    reassurance: "This 30-minute package is available at Ely Studio, with each exact time confirmed separately.",
    result: "You build consistency without committing to longer sessions straight away.",
    buttonLabel: "Book",
  },
};

const LOCAL_LESSON_OPTIONS: LocalLessonOption[] = getPublicLocalLessonProducts().map((product) => ({
  productKey: product.productKey,
  title: product.displayName,
  price: formatLocalLessonPrice(product.amountMinor),
  priceDetail: getLocalLessonPriceDetail(product.productKey),
  ...LOCAL_LESSON_OPTION_COPY[product.productKey],
}));

type CheckoutSuccessResponse = {
  url?: string;
  error?: string;
  code?: string;
};

type CalendarRecoveryStatus = Exclude<LocalLessonCalendarAdapterState["status"], "available">;

const LIGHT_SURFACE_ERROR_CLASS =
  "border-[#e8a7b4] bg-[#fff0f2] text-[#9f1239] break-words whitespace-pre-wrap [color-scheme:light]";

type LocalLessonPurchaseClientProps = {
  onCheckoutRedirect?: (url: string) => void;
  initialVenueId?: LiubaLocalLessonVenueId;
  sourcePage?: string;
  promotionCode?: string | null;
  laneModes?: LocalLessonLaneModes;
  calendarFirstEnabled?: boolean;
};

export type LocalLessonLaneMode = "automatic" | "manual" | "unavailable";
export type LocalLessonLaneModes = Record<LiubaLocalLessonVenueId, LocalLessonLaneMode>;

const DEFAULT_LANE_MODES: LocalLessonLaneModes = {
  ely_studio: "manual",
  bury_st_edmunds_studio: "manual",
};

function defaultCheckoutRedirect(url: string) {
  window.location.assign(url);
}

function getSupportingOptionIcon(option: LocalLessonOption) {
  if (option.productKey === "liuba_single_lesson") return MicrophoneStage;
  if (option.productKey === "liuba_lesson_pack_3" || option.productKey === "liuba_lesson_pack_4" || option.productKey === "liuba_lesson_pack_10") return CalendarDots;
  if (option.productKey === "liuba_half_hour_pack_6") return Clock;
  return UsersThree;
}

const DISCOVERY_PROGRAMME_FEATURES = [
  {
    number: "01",
    eyebrow: "Private direction",
    Icon: UserFocus,
    title: "Two private sessions with Liuba",
    badge: "Private 1:1",
    copy: "Begin with a 30-minute private Voice Discovery to understand your voice and goals. Finish with a 40-minute final Progress & Voice Blocker Review to assess what has changed and decide what comes next.",
    checklist: undefined,
    facts: ["70 minutes total", "Two private online sessions"],
    outcome: "Expert clarity, a focused plan and a personalised next step.",
  },
  {
    number: "02",
    eyebrow: "Liuba-led support",
    Icon: UsersThree,
    title: "Four more Liuba-led touchpoints",
    badge: "Online support",
    copy: "Attend two themed online group sessions and receive two personalised Video Feedback check-ins from Liuba between your private sessions.",
    checklist: undefined,
    facts: ["2 themed group sessions", "2 Video Feedback check-ins"],
    outcome: "Regular guidance and feedback to keep you moving forward.",
  },
  {
    number: "03",
    eyebrow: "Local practice",
    Icon: MapPin,
    title: "Two local exercise sessions",
    badge: "In person",
    copy: "Put Liuba’s plan into practice during two 50-minute Singing Attitude Method sessions with your confirmed local programme coach.",
    checklist: undefined,
    facts: ["100 minutes total", "2 × 50-minute sessions"],
    outcome: "Practical skill-building and supported technique practice in person.",
  },
  {
    number: "04",
    eyebrow: "Connected progress",
    Icon: CalendarDots,
    title: "One connected plan",
    badge: "Guided journey",
    copy: "Your private sessions, group support, Video Feedback and local practice are coordinated around your goals and progress.",
    checklist: [
      "Personalised high-impact coaching plan",
      "Clear actions between sessions",
      "Support across online and in-person touchpoints",
    ],
    facts: ["Usually completed over approximately 8 weeks", "Flexible scheduling"],
    outcome: "One clear direction and progress you can understand and feel.",
  },
] as const;

type SupportNeed = "unsure" | "focused" | "ongoing";

const SUPPORT_NEEDS: Array<{
  id: SupportNeed;
  title: string;
  copy: string;
  Icon: typeof UserFocus;
}> = [
  { id: "unsure", title: "New or unsure", copy: "I'm exploring my voice", Icon: UserFocus },
  { id: "focused", title: "One focused goal", copy: "I know what I want to achieve", Icon: Target },
  { id: "ongoing", title: "Ready for ongoing progress", copy: "I want a connected plan", Icon: TrendUp },
];

const vivianaTestimonial = getVideoTestimonial("viviana");

export function LocalLessonPurchaseClient({
  onCheckoutRedirect = defaultCheckoutRedirect,
  initialVenueId,
  sourcePage = "/local-lessons",
  promotionCode = null,
  laneModes = DEFAULT_LANE_MODES,
  calendarFirstEnabled = false,
}: LocalLessonPurchaseClientProps) {
  const [timeHold, setTimeHold] = useState<LocalLessonTimeHold | null>(null);
  const [holdSeconds, setHoldSeconds] = useState(0);
  const [activeProductKey, setActiveProductKey] = useState<LocalLessonProductKey | null>(null);
  const [selectedVenueId, setSelectedVenueId] = useState<LiubaLocalLessonVenueId | null>(initialVenueId ?? null);
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [phoneCountry, setPhoneCountry] = useState("GB");
  const [requestedDate, setRequestedDate] = useState("");
  const [requestedTime, setRequestedTime] = useState("");
  const [requestedAlternatives, setRequestedAlternatives] = useState("");
  const [calendarLink, setCalendarLink] = useState<string | null>(null);
  const [selectedCalBookingUid, setSelectedCalBookingUid] = useState<string | null>(null);
  const [isCalendarLoading, setIsCalendarLoading] = useState(false);
  const [manualFallbackActive, setManualFallbackActive] = useState(false);
  const [manualFallbackNotice, setManualFallbackNotice] = useState<string | null>(null);
  const [calendarRecoveryStatus, setCalendarRecoveryStatus] = useState<CalendarRecoveryStatus | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isVivianaVideoOpen, setIsVivianaVideoOpen] = useState(false);
  const [isVocalDiscoveryDetailsOpen, setIsVocalDiscoveryDetailsOpen] = useState(false);
  const [supportNeed, setSupportNeed] = useState<SupportNeed>("unsure");
  const [selectedPurchaseOption, setSelectedPurchaseOption] = useState<LocalLessonOption | null>(null);
  const [purchaseStep, setPurchaseStep] = useState<"review" | "details" | "calendar" | "preferences" | "consent">("review");
  const [checkoutConsent, setCheckoutConsent] = useState<CheckoutConsentInput>({ ...EMPTY_CHECKOUT_CONSENT });
  const [consentError, setConsentError] = useState<string | null>(null);
  const selectedVenue = LIUBA_LOCAL_LESSON_VENUES.find((venue) => venue.venue_id === selectedVenueId) ?? null;
  const isBuryVenue = selectedVenue?.venue_id === "bury_st_edmunds_studio";
  const isAutomaticFlow = Boolean(
    selectedVenue && (calendarFirstEnabled || laneModes[selectedVenue.venue_id] === "automatic") && !manualFallbackActive,
  );
  const availableVenues = LIUBA_LOCAL_LESSON_VENUES.filter(
    (venue) => laneModes[venue.venue_id] !== "unavailable",
  );
  const purchaseVenues = selectedPurchaseOption
    ? availableVenues.filter((venue) => (
        isLocalLessonProductAvailableAtVenue(selectedPurchaseOption.productKey, venue.venue_id)
      ))
    : availableVenues;
  const visibleOptions = LOCAL_LESSON_OPTIONS.filter((option) => (
    !selectedVenue || isLocalLessonProductAvailableAtVenue(option.productKey, selectedVenue.venue_id)
  ));
  const voiceDiscoveryOption = visibleOptions.find((option) => option.productKey === "liuba_intro_lesson") ?? LOCAL_LESSON_OPTIONS[0]!;
  const supportingOptions = visibleOptions.filter((option) => (
    option.productKey !== voiceDiscoveryOption?.productKey &&
    getLocalLessonProduct(option.productKey).publicGroup === "full_hour"
  ));
  const shortSessionOptions = visibleOptions.filter((option) => (
    getLocalLessonProduct(option.productKey).publicGroup === "short_session"
  ));
  const trackEvent = useTrackEvent();
  const packageTrackEvent = useTrackEvent({ includeAttribution: false });
  const decisionHelpPageRoute = sourcePage.split("?")[0] || "/local-lessons";
  const recommendedSupport = supportNeed === "focused"
    ? { href: "#one-hour-coaching", title: "1 Hour In-Person Coaching" }
    : supportNeed === "ongoing"
      ? { href: "#vocal-discovery-package", title: "Online + Local Programme" }
      : { href: "#voice-discovery", title: "Voice Discovery Lesson" };

  useEffect(() => {
    trackEvent("local_lessons_viewed", {
      product_type: "local_lessons",
      cta_position: "local_lesson_options",
      device_flow: window.innerWidth < 640 ? "mobile" : "desktop",
    });
    trackEvent("intro_lesson_product_view", {
      product_key: "liuba_intro_lesson",
      product_type: "local_lesson",
      page_type: "local_lessons",
      source_page: decisionHelpPageRoute,
      source_section: "local_lesson_options",
      funnel: "local_lessons",
      conversion_intent: "local_lesson_booking",
    });
  }, [decisionHelpPageRoute, trackEvent]);

  useEffect(() => {
    if (!initialVenueId) return;
    const venue = LIUBA_LOCAL_LESSON_VENUES.find((candidate) => candidate.venue_id === initialVenueId);
    trackEvent("booking_location_selected", {
      product_type: "local_lesson",
      location_slug: venue?.venue_slug ?? "unknown",
      venue_id: initialVenueId,
      source_page: decisionHelpPageRoute,
      funnel: "local_lessons",
      selection_source: "location_page",
    });
  }, [decisionHelpPageRoute, initialVenueId, trackEvent]);

  function purchaseAnalyticsProperties(option: LocalLessonOption, extra?: Record<string, string>) {
    return {
      ...getLocalLessonAnalyticsMetadata(option.productKey),
      product_type: option.productKey === "liuba_intro_lesson" ? "voice_discovery_lesson" : "local_lesson",
      location_slug: selectedVenue?.venue_slug ?? "not_selected",
      cta_position: "local_lesson_options",
      device_flow: typeof window !== "undefined" && window.innerWidth < 640 ? "mobile" : "desktop",
      ...extra,
    };
  }

  useEffect(() => {
    const viewedOptions = LOCAL_LESSON_OPTIONS.filter((option) => {
      const product = getLocalLessonProduct(option.productKey);
      return product.sessionCount > 1 && (!initialVenueId || isLocalLessonProductAvailableAtVenue(option.productKey, initialVenueId));
    });
    viewedOptions.forEach((option) => {
      trackEvent("local_package_viewed", purchaseAnalyticsProperties(option));
      if (getLocalLessonProduct(option.productKey).promoteFlexiblePayments) {
        trackEvent("flexible_payment_message_viewed", purchaseAnalyticsProperties(option));
      }
    });
    // Product-view events intentionally fire once for the initial page context.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialVenueId, trackEvent]);

  function releaseTimeHold() {
    if (timeHold) void calendarFirstRequest({ action: "release", flowId: timeHold.flowId, accessToken: timeHold.accessToken }).catch(() => undefined);
    setTimeHold(null);
  }

  useEffect(() => {
    if (!timeHold) return;
    const update = () => setHoldSeconds(Math.max(0, Math.floor((getCheckoutStartDeadline(timeHold) - Date.now()) / 1000)));
    update(); const timer = window.setInterval(update, 1000);
    return () => window.clearInterval(timer);
  }, [timeHold]);

  function openPurchaseReview(option: LocalLessonOption) {
    releaseTimeHold();
    setSelectedPurchaseOption(option);
    setPurchaseStep("review");
    setRequestedDate("");
    setRequestedTime("");
    setRequestedAlternatives("");
    setCalendarLink(null);
    setSelectedCalBookingUid(null);
    setManualFallbackActive(false);
    setManualFallbackNotice(null);
    setCalendarRecoveryStatus(null);
    setCheckoutConsent({ ...EMPTY_CHECKOUT_CONSENT });
    setConsentError(null);
    setErrorMessage(null);
    trackEvent("booking_started", purchaseAnalyticsProperties(option, {
      booking_status: "started",
      source_page: decisionHelpPageRoute,
    }));
    trackEvent("offer_selected", purchaseAnalyticsProperties(option));
    if (getLocalLessonProduct(option.productKey).sessionCount > 1) {
      trackEvent("local_package_selected", purchaseAnalyticsProperties(option));
    }
    if (option.productKey === "liuba_intro_lesson") {
      trackEvent("voice_discovery_selected", purchaseAnalyticsProperties(option));
    }
    trackEvent("purchase_review_opened", purchaseAnalyticsProperties(option));
  }

  function selectVenue(venueId: LiubaLocalLessonVenueId) {
    releaseTimeHold();
    const venue = LIUBA_LOCAL_LESSON_VENUES.find((candidate) => candidate.venue_id === venueId);
    setSelectedVenueId(venueId);
    setCalendarLink(null);
    setSelectedCalBookingUid(null);
    setManualFallbackActive(false);
    setManualFallbackNotice(null);
    setCalendarRecoveryStatus(null);
    setErrorMessage(null);
    trackEvent("booking_location_selected", {
      product_type: "local_lesson",
      location_slug: venue?.venue_slug ?? "unknown",
      venue_id: venueId,
      source_page: decisionHelpPageRoute,
      funnel: "local_lessons",
    });
  }

  function closePurchaseReview() {
    if (selectedCalBookingUid || activeProductKey) return;
    releaseTimeHold();
    if (selectedPurchaseOption) {
      trackEvent("purchase_review_closed", purchaseAnalyticsProperties(selectedPurchaseOption));
    }
    setSelectedPurchaseOption(null);
    setPurchaseStep("review");
    setRequestedDate("");
    setRequestedTime("");
    setRequestedAlternatives("");
    setCalendarLink(null);
    setSelectedCalBookingUid(null);
    setManualFallbackActive(false);
    setManualFallbackNotice(null);
    setCalendarRecoveryStatus(null);
    setCheckoutConsent({ ...EMPTY_CHECKOUT_CONSENT });
    setConsentError(null);
    setErrorMessage(null);
  }

  function continueToDetails() {
    if (!selectedPurchaseOption) return;
    if (!selectedVenue) {
      setErrorMessage("Please choose Ely Studio or Bury St Edmunds Studio before continuing.");
      return;
    }
    if (!isLocalLessonProductAvailableAtVenue(selectedPurchaseOption.productKey, selectedVenue.venue_id)) {
      setErrorMessage("This package is not available at the selected studio.");
      return;
    }
    setPurchaseStep("details");
    setErrorMessage(null);
    trackEvent("purchase_review_continued", purchaseAnalyticsProperties(selectedPurchaseOption));
    trackEvent("details_started", purchaseAnalyticsProperties(selectedPurchaseOption));
  }

  function getValidatedCustomerDetails() {
    const name = customerName.trim();
    const email = normalizeLocalLessonEmail(customerEmail);
    const phone = parsePhoneNumberFromString(customerPhone.trim(), phoneCountry as "GB" | "IE" | "US" | "CA" | "AU");
    const normalizedPhone = phone?.isValid() ? phone.number : null;
    if (!email) {
      setErrorMessage("Please add a valid email address, such as name@example.com.");
      return null;
    }
    if (!name || !normalizedPhone) {
      setErrorMessage("Please add your name, email and a valid phone number.");
      return null;
    }
    return { name, email, phone: normalizedPhone };
  }

  function activateManualFallback(status: "empty" | "error" = "error", message?: string) {
    setCalendarLink(null);
    setSelectedCalBookingUid(null);
    setManualFallbackActive(true);
    setCalendarRecoveryStatus(status);
    setManualFallbackNotice(message ?? (
      status === "empty"
        ? "No live times are available right now. You can share a preferred time instead; it remains a request until separately confirmed."
        : "Live availability could not be loaded. You can share a preferred time instead; it remains a request until separately confirmed."
    ));
    setPurchaseStep("preferences");
    setErrorMessage(null);
  }

  function recoverExpiredAutomaticBooking(message: string) {
    setActiveProductKey(null);
    setSelectedCalBookingUid(null);
    setManualFallbackActive(false);
    setManualFallbackNotice(null);
    setCalendarRecoveryStatus("expired");
    setConsentError(null);
    setErrorMessage(null);
    setPurchaseStep(calendarLink ? "calendar" : "details");
    if (!calendarLink) {
      setErrorMessage(message);
    }
  }

  async function continueAfterDetails() {
    if (!selectedPurchaseOption?.productKey || !selectedVenue) return;
    const customer = getValidatedCustomerDetails();
    if (!customer) return;

    if (calendarFirstEnabled) {
      releaseTimeHold(); setPurchaseStep("calendar"); setErrorMessage(null);
      trackEvent("details_completed", purchaseAnalyticsProperties(selectedPurchaseOption));
      return;
    }
    if (laneModes[selectedVenue.venue_id] === "automatic" && !manualFallbackActive) {
      setIsCalendarLoading(true);
      setErrorMessage(null);
      try {
        const response = await fetch("/api/local-lessons/calendar", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            productKey: selectedPurchaseOption.productKey,
            venueId: selectedVenue.venue_id,
            area: selectedVenue.area_label,
            customerName: customer.name,
            customerEmail: customer.email,
            customerPhone: customer.phone,
            sourcePage,
            sourceCta: selectedPurchaseOption.buttonLabel,
          }),
        });
        const data = await response.json().catch(() => null) as {
          calendarLink?: unknown;
          calendarState?: unknown;
          code?: unknown;
        } | null;
        const calendarState = adaptLocalLessonCalendarResponse(response.ok, data);
        if (calendarState.status === "available") {
          setCalendarLink(calendarState.calendarLink);
          setSelectedCalBookingUid(null);
          setCalendarRecoveryStatus(null);
          setPurchaseStep("calendar");
          trackEvent("details_completed", purchaseAnalyticsProperties(selectedPurchaseOption));
          trackEvent("package_calcom_opened", purchaseAnalyticsProperties(selectedPurchaseOption));
        } else if (calendarState.status === "empty" || calendarState.status === "error") {
          activateManualFallback(calendarState.status, calendarState.message);
        } else {
          setCalendarRecoveryStatus("expired");
          setErrorMessage(calendarState.message);
        }
      } catch {
        activateManualFallback("error");
      } finally {
        setIsCalendarLoading(false);
      }
      return;
    }

    setPurchaseStep("preferences");
    setErrorMessage(null);
    trackEvent("details_completed", purchaseAnalyticsProperties(selectedPurchaseOption, {
      booking_status: "manual_scheduling_required",
    }));
  }

  function continueToConsent() {
    if (!requestedDate || !requestedTime) {
      setErrorMessage("Please add your preferred lesson date and time.");
      return;
    }
    setErrorMessage(null);
    setPurchaseStep("consent");
  }

  useEffect(() => {
    trackEvent("decision_help_viewed", {
      option_type: "decision_help",
      cta_position: "decision_help",
      page_route: decisionHelpPageRoute,
      location_preference: initialVenueId ?? "not_selected",
    });
  }, [decisionHelpPageRoute, initialVenueId, trackEvent]);

  useEffect(() => {
    packageTrackEvent("vocal_discovery_package_viewed", {
      product_type: "vocal_discovery_package",
      cta_position: "decision_help",
      preferred_location: initialVenueId === "ely_studio"
        ? "ely"
        : initialVenueId === "bury_st_edmunds_studio"
          ? "bury_st_edmunds"
          : "either",
      device_flow: typeof window !== "undefined" && window.innerWidth < 640 ? "mobile" : "desktop",
      booking_stage: "package_viewed",
    });
  }, [initialVenueId, packageTrackEvent]);

  function trackDecisionHelpOption(optionType: "voice_discovery_lesson") {
    trackEvent("voice_discovery_option_selected", {
      option_type: optionType,
      cta_position: "decision_help",
      page_route: decisionHelpPageRoute,
      location_preference: selectedVenue?.venue_slug ?? "not_selected",
    });
  }

  function selectSupportNeed(need: SupportNeed) {
    setSupportNeed(need);
    trackEvent("decision_help_need_selected", {
      support_need: need,
      cta_position: "decision_help",
      page_route: decisionHelpPageRoute,
      location_preference: selectedVenue?.venue_slug ?? "not_selected",
    });
  }

  function openVocalDiscoveryDetails() {
    trackEvent("vocal_discovery_details_opened", {
      option_type: "vocal_discovery_package",
      cta_position: "package_details",
      page_route: decisionHelpPageRoute,
      location_preference: selectedVenue?.venue_slug ?? "not_selected",
    });
    setIsVocalDiscoveryDetailsOpen(true);
  }

  function closeVocalDiscoveryDetails() {
    trackEvent("vocal_discovery_details_closed", {
      option_type: "vocal_discovery_package",
      cta_position: "package_details",
      page_route: decisionHelpPageRoute,
      location_preference: selectedVenue?.venue_slug ?? "not_selected",
    });
    setIsVocalDiscoveryDetailsOpen(false);
  }

  async function startCheckout(option: LocalLessonOption) {
    if (!option.productKey || activeProductKey) return;
    const trackingPayload = {
      product_key: option.productKey,
      product_type: "local_lesson",
      page_type: "local_lessons",
      location: selectedVenue?.venue_slug ?? null,
      venue_id: selectedVenue?.venue_id ?? null,
      source_page: decisionHelpPageRoute,
      source_section: "local_lesson_options",
      source_cta: option.buttonLabel,
      funnel: "local_lessons",
      conversion_intent: "local_lesson_booking",
    };

    trackEvent("local_lesson_checkout_intent", trackingPayload);

    if (!selectedVenue) {
      setErrorMessage("Please choose Ely Studio or Bury St Edmunds Studio before starting checkout.");
      return;
    }

    const customer = getValidatedCustomerDetails();
    const automaticBookingUid = isAutomaticFlow ? selectedCalBookingUid : null;
    if (!customer) return;
    if (calendarFirstEnabled && (!timeHold || getCheckoutStartDeadline(timeHold) <= Date.now())) {
      releaseTimeHold(); setPurchaseStep("calendar"); setErrorMessage("The payment-start window has ended. Please select an available time again."); return;
    }
    if (!calendarFirstEnabled && (isAutomaticFlow ? !automaticBookingUid : !requestedDate || !requestedTime)) return;

    try {
      validateCheckoutConsentInput(checkoutConsent);
      if (calendarFirstEnabled && selectedVenue.venue_id === "ely_studio") requireElyEarlyPerformanceRequest(checkoutConsent);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Please review the consent choices.";
      setConsentError(message);
      if (message === "Please accept the Terms and Conditions to continue.") {
        trackEvent("checkout_terms_validation_failed");
      }
      return;
    }

    setActiveProductKey(option.productKey);
    setErrorMessage(null);
    trackEvent("payment_started", {
      ...trackingPayload,
      payment_status: "started",
    });
    trackEvent("checkout_requested", purchaseAnalyticsProperties(option));

    try {
      if (calendarFirstEnabled && timeHold) {
        const data = await calendarFirstRequest({ action: "checkout", flowId: timeHold.flowId, accessToken: timeHold.accessToken,
          consent: checkoutConsent, analyticsConsent: getCheckoutAnalyticsConsent(), sourcePage, sourceCta: option.buttonLabel });
        if (!data.checkoutUrl || !data.sessionId) throw new Error("Unable to start secure checkout.");
        sessionStorage.setItem(`sa-local-hold:${data.sessionId}`, JSON.stringify({ flowId: timeHold.flowId, accessToken: timeHold.accessToken }));
        trackEvent("local_lesson_checkout_started", trackingPayload); trackEvent("checkout_continued");
        onCheckoutRedirect(data.checkoutUrl); return;
      }
      const response = await fetch("/api/stripe/local-lessons/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          productKey: option.productKey,
          venueId: selectedVenue.venue_id,
          area: selectedVenue.area_label,
          customerName: customer.name,
          customerEmail: customer.email,
          customerPhone: customer.phone,
          ...(automaticBookingUid
            ? { calBookingUid: automaticBookingUid }
            : {
                requestedDate,
                requestedTime,
                ...(requestedAlternatives.trim() ? { requestedAlternatives: requestedAlternatives.trim() } : {}),
              }),
          sourcePage,
          sourceCta: option.buttonLabel,
          consent: checkoutConsent,
          analyticsConsent: getCheckoutAnalyticsConsent(),
          ...(promotionCode ? { promotionCode } : {}),
        }),
      });

      const data = (await response.json()) as CheckoutSuccessResponse;
      if (!response.ok) {
        if (isLocalBookingTimeExpiredCode(data.code)) {
          recoverExpiredAutomaticBooking(
            "That provisional time is no longer available. Please choose another live time.",
          );
          return;
        }
        throw new Error(data.error || "Unable to start secure checkout.");
      }

      if (!data.url) {
        throw new Error("Unable to start secure checkout.");
      }

      trackEvent("local_lesson_checkout_started", trackingPayload);
      trackEvent("checkout_continued");
      onCheckoutRedirect(data.url);
    } catch (error) {
      setActiveProductKey(null);
      setConsentError(error instanceof Error ? error.message : "Unable to start secure checkout.");
    }
  }

  function playVivianaTestimonial() {
    setIsVivianaVideoOpen(true);
    if (vivianaTestimonial) {
      trackEvent("local_student_transformation_video_play", {
        testimonial_id: vivianaTestimonial.id,
        testimonial_person: vivianaTestimonial.person,
        testimonial_title: vivianaTestimonial.title,
        source_page: decisionHelpPageRoute,
        source_section: "student_transformation_band",
      });
    }
  }

  const vivianaVideoModal =
    typeof document !== "undefined" && isVivianaVideoOpen && vivianaTestimonial
      ? createPortal(
          <div
            className="fixed inset-0 z-[80] flex items-center justify-center bg-[#10211e]/78 px-4 py-6 backdrop-blur-sm"
            role="dialog"
            aria-modal="true"
            aria-labelledby="viviana-testimonial-title"
          >
            <div className="w-full max-w-4xl overflow-hidden rounded-[24px] border border-[#d9c78f]/45 bg-[#fffaf2] p-3 shadow-[0_28px_90px_rgba(0,0,0,0.32)]">
              <div className="mb-3 flex items-center justify-between gap-3 px-1">
                <div>
                  <p className="text-[0.64rem] font-black uppercase tracking-[0.24em] text-[#8b6a2e]">Student transformation</p>
                  <h3 id="viviana-testimonial-title" className="mt-1 font-serif text-xl font-semibold text-[#123b34]">
                    Viviana on finding confidence in her voice
                  </h3>
                </div>
                <button
                  type="button"
                  onClick={() => setIsVivianaVideoOpen(false)}
                  className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#d8cab6] bg-white text-lg font-semibold text-[#13211f] transition hover:border-[#d9c78f]"
                  aria-label="Close Viviana testimonial video"
                >
                  <X size={18} aria-hidden />
                </button>
              </div>
              <div className="overflow-hidden rounded-[18px] border border-[#10211e]/10 bg-black">
                <div className="aspect-video w-full">
                  <iframe
                    className="h-full w-full"
                    src={`${vivianaTestimonial.embedUrl}&autoplay=1`}
                    title="Viviana testimonial video"
                    loading="lazy"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                  />
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )
      : null;

  return (
    <>
    <div className="space-y-8">
      <section id="decision-help" className="scroll-mt-24 text-[#13211f] sm:scroll-mt-28" aria-labelledby="decision-help-title">
        <header className="mx-auto max-w-4xl text-center">
          <p className="text-[0.7rem] font-black uppercase tracking-[0.3em] text-[#835b1c]">Your first lesson</p>
          <h2 id="decision-help-title" className="mt-3 font-serif text-[2.5rem] font-semibold leading-[1.02] tracking-tight text-[#123b34] sm:text-5xl">
            Choose your studio. Start with one lesson.
          </h2>
          <p className="mx-auto mt-3 max-w-3xl text-base leading-6 text-[#53615c]">
            Voice Discovery is the simplest first step: 30 minutes with Liuba, an emailed voice report and a recommendation. Choose your studio below. You can explore longer lessons and packages when you are ready.
          </p>
        </header>

        <fieldset className="mt-9">
          <legend className="text-[0.7rem] font-black uppercase tracking-[0.2em] text-[#34524c]">Choose your local studio</legend>
          <div className="mt-3 grid overflow-hidden rounded-[20px] border border-[#dfcfb6] bg-[#fffdf8] shadow-[0_14px_34px_rgba(49,39,24,0.055)] md:grid-cols-2">
            {availableVenues.map((venue, index) => {
              const isSelected = selectedVenueId === venue.venue_id;
              const address = venue.venue_name
                ? `${venue.venue_name} · ${formatLiubaVenueAddress(venue)}`
                : formatLiubaVenueAddress(venue);
              return (
                <button
                  key={venue.venue_id}
                  type="button"
                  onClick={() => selectVenue(venue.venue_id)}
                  className={`group relative flex min-h-[6.5rem] items-center gap-4 px-5 py-4 text-left transition focus-visible:z-10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-3px] focus-visible:outline-[#7a5114] ${
                    index > 0 ? "border-t border-[#dfcfb6] md:border-l md:border-t-0" : ""
                  } ${isSelected ? "bg-[#f6edda] shadow-[inset_0_0_0_2px_#123b34]" : "hover:bg-[#fff9ee]"}`}
                  aria-pressed={isSelected}
                >
                  <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 ${isSelected ? "border-[#123b34]" : "border-[#9d9d91]"}`} aria-hidden>
                    {isSelected ? <span className="h-3 w-3 rounded-full bg-[#123b34]" /> : null}
                  </span>
                  <MapPin size={25} weight={isSelected ? "fill" : "regular"} className={isSelected ? "text-[#123b34]" : "text-[#9a7028]"} aria-hidden />
                  <span className="min-w-0 flex-1">
                    <span className="block font-serif text-xl font-semibold leading-tight text-[#123b34] sm:text-2xl">{venue.public_name}</span>
                    <span className="mt-1 block text-sm font-semibold leading-5 text-[#5b6863]">{address}</span>
                  </span>
                </button>
              );
            })}
          </div>
          <p className="mt-3 flex items-center gap-2 text-sm font-semibold text-[#68736e]"><LockKey size={18} className="text-[#123b34]" aria-hidden />Your studio choice is carried into booking. You can change it before checkout.</p>
        </fieldset>

        <fieldset className="mt-8">
          <legend className="text-[0.7rem] font-black uppercase tracking-[0.2em] text-[#34524c]">Where are you right now?</legend>
          <div className="mt-3 grid gap-3 md:grid-cols-3">
            {SUPPORT_NEEDS.map(({ id, title, copy, Icon }) => {
              const isSelected = supportNeed === id;
              return (
                <button
                  key={id}
                  type="button"
                  aria-pressed={isSelected}
                  onClick={() => selectSupportNeed(id)}
                  className={`flex min-h-[5.5rem] items-center gap-3 rounded-[18px] border px-4 py-3 text-left transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#7a5114] ${isSelected ? "border-[#123b34] bg-[#123b34] text-[#fffaf2] shadow-[0_14px_30px_rgba(18,59,52,0.16)]" : "border-[#dfcfb6] bg-[#fffdf8] text-[#123b34] hover:border-[#b7892d] hover:bg-[#fff9ee]"}`}
                >
                  <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full border ${isSelected ? "border-[#f1d486]/75 text-[#f1d486]" : "border-[#d9c78f] text-[#9a7028]"}`} aria-hidden><Icon size={24} /></span>
                  <span><span className="block font-serif text-lg font-semibold leading-tight">{title}</span><span className={`mt-1 block text-xs font-semibold leading-5 ${isSelected ? "text-white/72" : "text-[#68736e]"}`}>{copy}</span></span>
                </button>
              );
            })}
          </div>
          <p aria-live="polite" className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm font-semibold text-[#68736e]">
            <span>Your recommended next step:</span>
            <Link href={recommendedSupport.href} className="inline-flex items-center gap-1.5 font-bold text-[#123b34] underline decoration-[#b7892d] underline-offset-4 transition hover:text-[#7a5114]">
              {recommendedSupport.title}<ArrowRight size={15} aria-hidden />
            </Link>
          </p>
        </fieldset>

        <article
          id="voice-discovery"
          data-testid="voice-discovery-section"
          aria-labelledby="voice-discovery-title"
          className={`mt-6 scroll-mt-24 overflow-hidden rounded-[22px] border bg-[#fffdf8] shadow-[0_18px_48px_rgba(49,39,24,0.075)] sm:scroll-mt-28 lg:grid lg:grid-cols-[minmax(0,1.35fr)_minmax(18rem,0.65fr)] ${supportNeed === "unsure" ? "border-[#b7892d] ring-1 ring-[#b7892d]/35" : "border-[#dfcfb6]"}`}
        >
          <div className="p-6 sm:p-8">
            <p className="text-[0.68rem] font-black uppercase tracking-[0.2em] text-[#835b1c]">{supportNeed === "unsure" ? "Recommended for you" : "Supportive first step"}</p>
            <h3 id="voice-discovery-title" className="mt-4 font-serif text-3xl font-semibold leading-tight text-[#123b34] sm:text-[2.35rem]">Voice Discovery Lesson</h3>
            <p className="mt-3 max-w-2xl text-base leading-7 text-[#53615c]">Find out what is making singing feel difficult, try practical guidance with Liuba, and receive a personalised voice report. One lesson, with no commitment to a package.</p>
            <div className="mt-6 grid gap-3">
              {[
                [MicrophoneStage, "Private 30-minute local session with Liuba"],
                [FileText, "Emailed voice report"],
                [CheckCircle, "Personalised recommendation"],
              ].map(([Icon, copy]) => (
                <div key={String(copy)} className="flex items-center gap-3 text-sm font-bold leading-5 text-[#34524c]"><Icon size={23} className="shrink-0 text-[#9a7028]" aria-hidden /><span>{String(copy)}</span></div>
              ))}
            </div>
            <button
              type="button"
              aria-haspopup="dialog"
              onClick={() => openPurchaseReview(voiceDiscoveryOption)}
              className="mt-6 inline-flex min-h-11 items-center gap-2 text-sm font-bold text-[#123b34] underline decoration-[#b7892d] underline-offset-4 transition hover:text-[#7a5114]"
            >
              Learn more about Voice Discovery<ArrowRight size={16} aria-hidden />
            </button>
          </div>
          <div className="flex flex-col justify-center border-t border-[#dfcfb6] bg-[#fffaf2] p-6 text-center sm:p-8 lg:border-l lg:border-t-0">
            <p className="font-serif text-5xl font-semibold tracking-tight text-[#123b34]"><span className="sr-only">Price: </span>{voiceDiscoveryOption.price}</p>
            <p className="mx-auto mt-3 max-w-md text-sm font-semibold leading-6 text-[#61706b]">£75 at your selected local venue: a private 30-minute session with Liuba, plus your emailed voice report and recommendation.</p>
            <button
              type="button"
              aria-label="Book Voice Discovery Lesson"
              onClick={() => {
                trackDecisionHelpOption("voice_discovery_lesson");
                openPurchaseReview(voiceDiscoveryOption);
              }}
              disabled={Boolean(activeProductKey)}
              className="btn-premium mt-6 inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-[14px] bg-[#123b34] px-5 py-3 text-sm font-semibold text-[#fffaf2] transition hover:bg-[#1b5147] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#7a5114] disabled:cursor-not-allowed disabled:opacity-70"
            >
              {activeProductKey === voiceDiscoveryOption.productKey ? "Starting secure checkout..." : "Book Voice Discovery"}<ArrowRight size={18} aria-hidden />
            </button>
            <p className="mt-4 inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-[#61706b]">
              Secure payment powered by <StripeLogo size={18} weight="fill" className="text-[#635bff]" aria-hidden /> <span className="font-bold text-[#635bff]">Stripe</span>
            </p>
            <div className="mt-3 flex flex-wrap items-center justify-center gap-2" aria-label="Payment methods available at secure checkout">
              <span className="inline-flex h-8 items-center gap-1 rounded-md border border-[#dfcfb6] bg-white px-2 text-xs font-bold text-black"><AppleLogo size={15} weight="fill" aria-hidden /> Pay</span>
              <span className="inline-flex h-8 items-center gap-1 rounded-md border border-[#dfcfb6] bg-white px-2 text-xs font-bold text-[#3c4043]"><GoogleLogo size={15} weight="fill" className="text-[#4285f4]" aria-hidden /> Pay</span>
              <span className="inline-flex h-8 items-center rounded-md border border-[#dfcfb6] bg-white px-2 text-xs font-black italic tracking-tight text-[#1a1f71]">VISA</span>
              <span className="inline-flex h-8 items-center gap-1 rounded-md border border-[#dfcfb6] bg-white px-2 text-xs font-bold text-[#1d1d1b]"><span className="relative h-3.5 w-5" aria-hidden><span className="absolute left-0 h-3.5 w-3.5 rounded-full bg-[#eb001b]" /><span className="absolute right-0 h-3.5 w-3.5 rounded-full bg-[#f79e1b] mix-blend-multiply" /></span> Mastercard</span>
            </div>
            <p className="mt-2 text-xs leading-5 text-[#59645f]">Apple Pay and Google Pay availability depends on your device and browser.</p>
          </div>
        </article>

        <article
          id="vocal-discovery-package"
          data-testid="vocal-discovery-package-section"
          aria-labelledby="vocal-discovery-package-title"
          className={`mt-5 scroll-mt-24 overflow-hidden rounded-[22px] border border-[#d9c78f]/55 bg-[#0d3c34] p-4 text-[#fffaf2] shadow-[0_22px_58px_rgba(13,60,52,0.18)] sm:scroll-mt-28 sm:p-5 lg:grid lg:grid-cols-[minmax(17rem,0.8fr)_minmax(0,1.2fr)] lg:gap-5 ${supportNeed === "ongoing" ? "ring-2 ring-[#d9c78f] ring-offset-2 ring-offset-[#fffaf2]" : ""}`}
        >
          <div className="flex flex-col items-center justify-center text-center">
            <p className="text-[0.68rem] font-black uppercase tracking-[0.2em] text-[#e0c477]">{supportNeed === "ongoing" ? "Recommended for you" : "Connected programme"}</p>
            <p className="mt-3 font-serif text-3xl font-semibold leading-[1.05] text-[#f1d486] sm:text-[2rem]">Want a connected programme?</p>
            <h3 id="vocal-discovery-package-title" className="mt-3 font-serif text-xl font-semibold leading-tight text-[#fffaf2]">Online + Local Programme</h3>
            <p className="mt-2 text-sm leading-6 text-white/76">Vocal Discovery Package: eight coordinated touchpoints, usually completed over approximately eight weeks.</p>
            <p className="mt-3 flex flex-col items-center font-serif font-semibold tracking-tight text-[#f1d486]"><span className="text-5xl leading-none sm:text-6xl"><span className="sr-only">Price: </span>£349</span><span className="mt-1 text-xs font-black uppercase tracking-[0.22em] text-[#f5df9f]">total</span></p>
            <p className="mt-3 max-w-md text-sm font-bold leading-5 text-[#f5df9f]">Six Liuba-led touchpoints. Two local exercise sessions. One connected plan.</p>
            <div className="mt-4 w-full max-w-sm">
              <VocalDiscoveryPurchaseFlow
                key={selectedVenueId ?? "either"}
                initialLocation={selectedVenue?.venue_slug === "ely" ? "ely" : selectedVenue?.venue_slug === "bury-st-edmunds" ? "bury_st_edmunds" : "either"}
                onCheckoutRedirect={onCheckoutRedirect}
              />
            </div>
            <p className="mt-3 inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-white/70">
              Secure payment powered by <StripeLogo size={18} weight="fill" className="text-[#a9a1ff]" aria-hidden /> <span className="font-bold text-[#d8d4ff]">Stripe</span>
            </p>
            <div className="mt-2 flex flex-wrap items-center justify-center gap-1.5" aria-label="Payment methods available at secure checkout">
              <span className="inline-flex h-8 items-center gap-1 rounded-md bg-white px-2 text-xs font-bold text-black"><AppleLogo size={15} weight="fill" aria-hidden /> Pay</span>
              <span className="inline-flex h-8 items-center gap-1 rounded-md bg-white px-2 text-xs font-bold text-[#3c4043]"><GoogleLogo size={15} weight="fill" className="text-[#4285f4]" aria-hidden /> Pay</span>
              <span className="inline-flex h-8 items-center rounded-md bg-white px-2 text-xs font-black italic tracking-tight text-[#1a1f71]">VISA</span>
              <span className="inline-flex h-8 items-center gap-1 rounded-md bg-white px-2 text-xs font-bold text-[#1d1d1b]"><span className="relative h-3.5 w-5" aria-hidden><span className="absolute left-0 h-3.5 w-3.5 rounded-full bg-[#eb001b]" /><span className="absolute right-0 h-3.5 w-3.5 rounded-full bg-[#f79e1b] mix-blend-multiply" /></span> Mastercard</span>
            </div>
            <div data-testid="vocal-discovery-flexible-payment-options" className="mt-3 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 rounded-[12px] border border-[#e7d8bd] bg-white px-3 py-2 text-center text-xs font-semibold leading-4 text-[#31564d] shadow-[0_5px_14px_rgba(0,0,0,0.1)]">
              <span>{FLEXIBLE_PAYMENT_DISCLAIMER}</span>

            </div>
            <p className="mt-1 text-xs leading-4 text-white/58">Apple Pay and Google Pay availability depends on your device and browser.</p>
            <button
              type="button"
              aria-haspopup="dialog"
              aria-expanded={isVocalDiscoveryDetailsOpen}
              onClick={openVocalDiscoveryDetails}
              className="mt-2 inline-flex min-h-11 w-fit items-center gap-2 text-sm font-bold text-[#f5df9f] underline decoration-[#e0c477] underline-offset-4 transition hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#d9c78f]"
            >
              See everything included<ArrowRight size={16} aria-hidden />
            </button>
            <p className="mt-1 max-w-[26rem] text-xs font-semibold leading-5 text-white/58">Choose a preferred local location before payment. Liuba confirms the coach, studio and remaining dates after your first online session.</p>
          </div>
          <div data-testid="vocal-discovery-inclusion-grid" className="mt-5 grid gap-2.5 sm:grid-cols-2 lg:mt-0">
            {DISCOVERY_PROGRAMME_FEATURES.map(({ number, eyebrow, Icon, title, badge, copy, checklist, facts, outcome }) => (
              <article key={title} data-testid={`vocal-discovery-inclusion-${number}`} className="flex flex-col rounded-[18px] border border-[#e7d8bd] bg-[#fffdf8] p-3.5 text-[#123b34] shadow-[0_12px_26px_rgba(3,25,21,0.12)] sm:p-4">
                <div className="flex items-center gap-2.5">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#f4d68f] text-[0.82rem] font-black text-[#123b34]">{number}</span>
                  <p className="min-w-0 flex-1 text-[0.62rem] font-black uppercase tracking-[0.18em] text-[#31564d]">{eyebrow}</p>
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[#d3bf92] bg-[#fffaf2] text-[#123b34]" aria-hidden><Icon size={20} /></span>
                </div>
                <div className="mt-3 flex flex-wrap items-start justify-between gap-1.5">
                  <h4 className="font-serif text-[1.18rem] font-semibold leading-[1.08] text-[#123b34]">{title}</h4>
                  <span className="rounded-full border border-[#d9c78f] bg-[#fff5df] px-2 py-0.5 text-[0.58rem] font-black uppercase tracking-[0.1em] text-[#73521b]">{badge}</span>
                </div>
                <p className="mt-2 text-[0.82rem] font-medium leading-[1.35] text-[#455a54]">{copy}</p>
                {checklist ? (
                  <ul className="mt-2 space-y-1 text-[0.78rem] font-semibold leading-[1.3] text-[#31564d]">
                    {checklist.map((item) => <li key={item} className="flex gap-1.5"><CheckCircle size={15} weight="fill" className="mt-0.5 shrink-0 text-[#3f765e]" aria-hidden /><span>{item}</span></li>)}
                  </ul>
                ) : null}
                <div className="mt-auto grid grid-cols-2 gap-1.5 rounded-[12px] bg-[#f1f2ed] px-2.5 py-2 text-[0.7rem] font-bold leading-[1.25] text-[#31564d]">
                  {facts.map((fact) => <span key={fact}>{fact}</span>)}
                </div>
                <p className="mt-2 rounded-[10px] bg-[#fff1d5] px-2.5 py-1.5 text-[0.72rem] font-semibold leading-[1.3] text-[#4f4532]"><span className="font-black text-[#123b34]">Outcome:</span> {outcome}</p>
              </article>
            ))}
          </div>
        </article>

        <section className="mt-9" aria-labelledby="experienced-options-title">
          <h3 id="experienced-options-title" className="font-serif text-3xl font-semibold text-[#123b34]">Already know what you need?</h3>
          <p className="mt-2 text-sm leading-6 text-[#61706b]">Explore focused in-person coaching options at your chosen studio.</p>
          <div className="mt-4 overflow-hidden rounded-[20px] border border-[#dfcfb6] bg-[#fffdf8] shadow-[0_16px_42px_rgba(49,39,24,0.065)]">
            {supportingOptions.map((option) => {
              const OptionIcon = getSupportingOptionIcon(option);
              const product = getLocalLessonProduct(option.productKey);
              const isLoading = activeProductKey === option.productKey;
              const isRecommended = supportNeed === "focused" && option.productKey === "liuba_single_lesson";
              return (
                <article id={option.productKey === "liuba_single_lesson" ? "one-hour-coaching" : undefined} key={option.productKey ?? option.title} className={`scroll-mt-24 grid gap-4 border-b border-[#e4d8c6] p-4 last:border-b-0 sm:scroll-mt-28 sm:p-5 md:grid-cols-[minmax(0,1fr)_7rem_minmax(11rem,auto)_auto] md:items-center ${isRecommended ? "bg-[#f6edda] shadow-[inset_4px_0_0_#b7892d]" : ""}`}>
                  <div className="flex min-w-0 gap-4">
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-[#d9c78f] bg-[#fffaf2] text-[#123b34]" aria-hidden><OptionIcon size={24} /></span>
                    <div className="min-w-0">
                      {isRecommended ? <p className="mb-1 text-[0.62rem] font-black uppercase tracking-[0.18em] text-[#835b1c]">Recommended for your goal</p> : null}
                      <p className="font-serif text-xl font-semibold leading-tight text-[#123b34]">{option.title}</p>
                      <p className="mt-1 text-sm leading-5 text-[#61706b]">{option.summary}</p>
                      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs font-semibold leading-4 text-[#68736e]">
                        <span>{option.priceDetail}</span>
                        {product.promoteFlexiblePayments ? (
                          <span className="inline-flex flex-wrap items-center gap-2">
                            <span>{FLEXIBLE_PAYMENT_DISCLAIMER}</span>
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </div>
                  <div className="text-center md:text-left">
                    <p className="font-serif text-3xl font-semibold text-[#123b34]">{option.price}</p>
                  </div>
                  <button
                    type="button"
                    aria-label={`Book ${option.title}`}
                    onClick={() => openPurchaseReview(option)}
                    disabled={Boolean(activeProductKey)}
                    className="inline-flex min-h-12 items-center justify-center gap-2 rounded-[13px] bg-[#123b34] px-4 text-center text-sm font-bold text-white transition hover:bg-[#1b5147] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#7a5114] disabled:opacity-60"
                  >
                    {isLoading ? "Opening checkout..." : option.buttonLabel}<ArrowRight size={17} aria-hidden />
                  </button>
                  <button
                    type="button"
                    aria-haspopup="dialog"
                    aria-label={`Learn more about ${option.title}`}
                    onClick={() => openPurchaseReview(option)}
                    className="inline-flex min-h-11 items-center justify-center gap-1.5 text-sm font-bold text-[#123b34] underline decoration-[#b7892d] underline-offset-4 transition hover:text-[#7a5114]"
                  >
                    Learn more<ArrowRight size={15} aria-hidden />
                  </button>
                </article>
              );
            })}
          </div>
          {shortSessionOptions.length > 0 ? (
            <div className="mt-6">
              <h4 className="font-serif text-2xl font-semibold text-[#123b34]">Shorter, more frequent sessions — Ely only</h4>
              <p className="mt-2 text-sm leading-6 text-[#61706b]">Six shorter appointments for students who prefer a lighter, more frequent rhythm. This is a distinct format from the 3 × 60-minute package.</p>
              <div className="mt-3 overflow-hidden rounded-[20px] border border-[#dfcfb6] bg-[#fffdf8] shadow-[0_16px_42px_rgba(49,39,24,0.065)]">
                {shortSessionOptions.map((option) => {
                  const isLoading = activeProductKey === option.productKey;
                  return (
                    <article key={option.productKey} className="grid gap-4 p-4 sm:p-5 md:grid-cols-[minmax(0,1fr)_7rem_minmax(11rem,auto)_auto] md:items-center">
                      <div className="flex min-w-0 gap-4">
                        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-[#d9c78f] bg-[#fffaf2] text-[#123b34]" aria-hidden><Clock size={24} /></span>
                        <div className="min-w-0">
                          <p className="font-serif text-xl font-semibold leading-tight text-[#123b34]">{option.title}</p>
                          <p className="mt-1 text-sm leading-5 text-[#61706b]">{option.summary}</p>
                          <p className="mt-2 text-xs font-semibold leading-4 text-[#68736e]">{option.priceDetail}</p>
                        </div>
                      </div>
                      <div className="text-center md:text-left"><p className="font-serif text-3xl font-semibold text-[#123b34]">{option.price}</p></div>
                      <button type="button" aria-label={`Book ${option.title}`} onClick={() => openPurchaseReview(option)} disabled={Boolean(activeProductKey)} className="inline-flex min-h-12 items-center justify-center gap-2 rounded-[13px] bg-[#123b34] px-4 text-center text-sm font-bold text-white transition hover:bg-[#1b5147] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#7a5114] disabled:opacity-60">
                        {isLoading ? "Opening checkout..." : option.buttonLabel}<ArrowRight size={17} aria-hidden />
                      </button>
                      <button type="button" aria-haspopup="dialog" aria-label={`Learn more about ${option.title}`} onClick={() => openPurchaseReview(option)} className="inline-flex min-h-11 items-center justify-center gap-1.5 text-sm font-bold text-[#123b34] underline decoration-[#b7892d] underline-offset-4 transition hover:text-[#7a5114]">
                        Learn more<ArrowRight size={15} aria-hidden />
                      </button>
                    </article>
                  );
                })}
              </div>
            </div>
          ) : null}
          <p className="mt-3 text-xs font-semibold leading-5 text-[#68736e]">{calendarFirstEnabled ? "Hold your first available appointment before payment. Payment confirms the calendar booking; Bury lessons remain provisional until the room is checked and confirmed in writing. Arrange the remaining appointments in a pack separately." : `${LOCAL_LESSON_MANUAL_ORDERING_COPY} Bury times also require a separate room check.`}</p>
        </section>

        <div className="mt-7 grid overflow-hidden rounded-[20px] border border-[#dfcfb6] bg-[#fffdf8] shadow-[0_14px_34px_rgba(49,39,24,0.055)] md:grid-cols-3">
          {[
            [ShieldCheck, "No audition", "A judgement-free space. Adults and beginners are welcome."],
            [LockKey, "Secure checkout", "Your details and booking are handled carefully and privately."],
          ].map(([Icon, title, copy], index) => (
            <div key={String(title)} className={`flex min-h-[8rem] items-center gap-4 p-5 ${index > 0 ? "border-t border-[#e4d8c6] md:border-l md:border-t-0" : ""}`}>
              <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-[#d9c78f] text-[#9a7028]" aria-hidden><Icon size={27} /></span>
              <div><p className="font-serif text-lg font-semibold text-[#123b34]">{String(title)}</p><p className="mt-1 text-xs font-semibold leading-5 text-[#68736e]">{String(copy)}</p></div>
            </div>
          ))}
          <Link href={`/contact?reason=Lesson%20choice&studio=${selectedVenue?.venue_slug ?? "undecided"}`} className="flex min-h-[8rem] items-center gap-4 border-t border-[#e4d8c6] p-5 transition hover:bg-[#fff9ee] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-3px] focus-visible:outline-[#7a5114] md:border-l md:border-t-0">
            <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-[#d9c78f] text-[#9a7028]" aria-hidden><ChatCircleDots size={27} /></span>
            <span><span className="block font-serif text-lg font-semibold text-[#123b34]">Need help choosing?</span><span className="mt-1 block text-xs font-semibold leading-5 text-[#68736e]">We&apos;re here to help. Just get in touch.</span></span>
          </Link>
        </div>
      </section>

      <div className="grid gap-4 overflow-hidden rounded-[26px] border border-[#e2d3bc] bg-[#fff8ed] p-4 shadow-[0_20px_58px_rgba(49,39,24,0.07)] sm:grid-cols-[11rem_minmax(0,1fr)_auto] sm:items-center sm:p-5">
        <div className="relative min-h-[8rem] overflow-hidden rounded-[18px] bg-[#10211e] sm:min-h-[9rem]">
          <Image
            src="/images/testimonials/youtube/viviana.jpg"
            alt="Viviana, Singing Attitude student testimonial"
            fill
            sizes="11rem"
            className="object-cover"
          />
          <button
            type="button"
            data-amp-href={vivianaTestimonial?.embedUrl}
            onClick={playVivianaTestimonial}
            className="absolute inset-0 flex items-center justify-center bg-black/10 transition hover:bg-black/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-4px] focus-visible:outline-white"
            aria-label="Play Viviana's story"
          >
            <span className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/30 bg-white/20 text-lg text-white shadow-[0_10px_24px_rgba(0,0,0,0.24)] backdrop-blur-sm">
              <Play size={20} weight="fill" aria-hidden />
            </span>
          </button>
        </div>
        <div>
          <p className="text-[0.68rem] font-black uppercase tracking-[0.24em] text-[#7a5114]">Student transformation</p>
          <p className="mt-2 font-serif text-2xl font-semibold leading-tight text-[#123b34]">
            Feel more confident, less self-conscious, and more at home in your voice.
          </p>
          <p className="mt-2 text-sm font-semibold leading-6 text-[#5c6964]">
            Viviana, Singing Attitude student testimonial. If confidence is the reason you are hesitating, Voice Discovery gives you a calm first step and a written record afterwards.
          </p>
        </div>
        <button
          type="button"
          data-amp-href={vivianaTestimonial?.embedUrl}
          onClick={playVivianaTestimonial}
          className="btn-premium inline-flex min-h-12 items-center justify-center rounded-full bg-[#d9c78f] px-5 py-3 text-sm font-semibold text-[#13211f] transition hover:bg-[#e4d39c] disabled:cursor-not-allowed disabled:opacity-70"
        >
          Watch Viviana&apos;s story
        </button>
      </div>

      {errorMessage ? (
        <div className={`rounded-[20px] border p-4 ${LIGHT_SURFACE_ERROR_CLASS}`} role="alert">
          <p className="text-sm font-semibold">{errorMessage}</p>
        </div>
      ) : null}
    </div>
    <VocalDiscoveryDetailsDialog open={isVocalDiscoveryDetailsOpen} onClose={closeVocalDiscoveryDetails} />
    <PurchaseReviewDialog
      open={Boolean(selectedPurchaseOption)}
      onClose={closePurchaseReview}
      dismissible={!selectedCalBookingUid && !activeProductKey}
      title={purchaseStep === "review"
        ? selectedPurchaseOption?.title ?? "Review your purchase"
        : purchaseStep === "details"
          ? "Your details"
          : purchaseStep === "calendar"
            ? "Choose an available slot"
            : purchaseStep === "preferences"
            ? "Your preferred lesson time"
            : isAutomaticFlow ? "Review terms before payment" : "Review terms and preferences"}
      description={purchaseStep === "review"
        ? "Check the format, price and next steps before continuing."
        : purchaseStep === "details"
          ? isAutomaticFlow
            ? "Add the details needed to load live availability and prepare secure payment."
            : "Add only the details needed for payment and manual scheduling."
          : purchaseStep === "calendar"
            ? calendarFirstEnabled ? "Select a time to hold temporarily before payment." : "Choose a provisional time from Liuba's live availability. Payment and final confirmation remain separate steps."
            : purchaseStep === "preferences"
            ? "Tell us your preferred time. It is a request, not a reserved or confirmed appointment."
            : isAutomaticFlow
              ? calendarFirstEnabled ? "Review the required terms before payment. Your hold is temporary; Bury room confirmation follows separately." : "Review the required terms before secure payment for your selected provisional time. Your appointment is final only after separate confirmation."
              : "Accept the required terms and, if you wish, set a separate media preference before secure payment."}
      footer={selectedPurchaseOption ? purchaseStep === "review" ? (
        <div className="grid gap-2 sm:grid-cols-[auto_1fr]">
          <button type="button" onClick={closePurchaseReview} className="min-h-12 rounded-[14px] border border-[#d8cab6] px-5 text-sm font-bold text-[#123b34]">Go back</button>
          <button type="button" onClick={continueToDetails} className="min-h-12 rounded-[14px] bg-[#123b34] px-5 text-sm font-bold text-white">Continue to your details</button>
        </div>
      ) : purchaseStep === "details" ? (
        <div className="grid gap-2 sm:grid-cols-[auto_1fr]">
          <button type="button" onClick={() => { setPurchaseStep("review"); setErrorMessage(null); }} className="min-h-12 rounded-[14px] border border-[#d8cab6] px-5 text-sm font-bold text-[#123b34]">Go back</button>
          <button type="button" onClick={continueAfterDetails} disabled={isCalendarLoading || Boolean(activeProductKey)} className="min-h-12 rounded-[14px] bg-[#123b34] px-5 text-sm font-bold text-white disabled:opacity-60">
            {isCalendarLoading ? "Loading Liuba's calendar…" : isAutomaticFlow ? "Continue to Liuba's calendar" : "Continue to time preferences"}
          </button>
        </div>
      ) : purchaseStep === "calendar" ? (
        <button type="button" onClick={() => { releaseTimeHold(); setPurchaseStep("details"); setCalendarLink(null); setSelectedCalBookingUid(null); setCalendarRecoveryStatus(null); }} className="min-h-12 w-full rounded-[14px] border border-[#d8cab6] px-5 text-sm font-bold text-[#123b34]">Go back to your details</button>
      ) : purchaseStep === "preferences" ? (
        <div className="grid gap-2 sm:grid-cols-[auto_1fr]">
          <button type="button" onClick={() => { setPurchaseStep("details"); setErrorMessage(null); }} className="min-h-12 rounded-[14px] border border-[#d8cab6] px-5 text-sm font-bold text-[#123b34]">Go back to your details</button>
          <button type="button" onClick={continueToConsent} className="min-h-12 rounded-[14px] bg-[#123b34] px-5 text-sm font-bold text-white">Continue to terms</button>
        </div>
      ) : (
        <div className="grid gap-2 sm:grid-cols-[auto_1fr]">
          {selectedCalBookingUid ? (
            <Link href={`/contact?reason=Booking%20question&studio=${selectedVenue?.venue_slug ?? "undecided"}`} className="inline-flex min-h-12 items-center justify-center rounded-[14px] border border-[#d8cab6] px-5 text-sm font-bold text-[#123b34]">Contact support</Link>
          ) : (
            <button type="button" onClick={() => { releaseTimeHold(); setPurchaseStep(calendarFirstEnabled ? "calendar" : "preferences"); setConsentError(null); }} disabled={Boolean(activeProductKey)} className="min-h-12 rounded-[14px] border border-[#d8cab6] px-5 text-sm font-bold text-[#123b34] disabled:opacity-60">{calendarFirstEnabled ? "Choose another time" : "Go back to time preferences"}</button>
          )}
          <button type="button" onClick={() => startCheckout(selectedPurchaseOption)} disabled={Boolean(activeProductKey) || (calendarFirstEnabled && (!timeHold || holdSeconds <= 0))} className="min-h-12 rounded-[14px] bg-[#123b34] px-5 text-sm font-bold text-white disabled:opacity-60">
            {activeProductKey ? "Opening secure payment…" : `Continue to secure payment — ${selectedPurchaseOption.price}`}
          </button>
        </div>
      ) : undefined}
    >
      {selectedPurchaseOption ? purchaseStep === "review" ? (
        <div className="space-y-5">
          <div className="grid gap-3 rounded-[18px] border border-[#d9c78f]/65 bg-[#fff5df] p-4 sm:grid-cols-2">
            <div>
              <p className="text-xs font-black uppercase tracking-[0.18em] text-[#9b6b19]">Total price</p>
              <p className="mt-1 font-serif text-4xl font-semibold text-[#123b34]">{selectedPurchaseOption.price}</p>
              <p className="text-sm font-semibold text-[#53615c]">One-time payment</p>
            </div>
            <div>
              <p className="text-xs font-black uppercase tracking-[0.18em] text-[#9b6b19]">Format</p>
              <p className="mt-2 text-lg font-bold text-[#123b34]">{selectedVenue ? `In person · ${getLiubaVenueDisplayName(selectedVenue)}` : "Choose a local studio"}</p>
              <p className="text-sm text-[#53615c]">{selectedPurchaseOption.priceDetail}</p>
            </div>
          </div>
          <fieldset className="rounded-[18px] border border-[#e2d3bc] bg-white p-4">
              <legend className="px-1 font-bold text-[#123b34]">{selectedPurchaseOption.productKey === "liuba_intro_lesson" ? "Choose your preferred local studio" : "Choose your studio"}</legend>
              <p className="mt-1 text-sm leading-6 text-[#53615c]">{calendarFirstEnabled ? "Choose your studio, then select and temporarily hold an available time before payment." : isAutomaticFlow
                ? "Choose the venue you want, then select a provisional time from Liuba's live availability before secure payment."
                : "Choose the venue you want before secure payment. Your preferred time is confirmed manually afterward."}</p>
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                {purchaseVenues.map((venue) => {
                  const checked = selectedVenueId === venue.venue_id;
                  return (
                    <label key={venue.venue_id} className={`flex cursor-pointer items-start gap-3 rounded-[14px] border p-3 transition ${checked ? "border-[#b7892d] bg-[#fff5df]" : "border-[#d8cab6] bg-white hover:border-[#b7892d]/70"}`}>
                      <input
                        type="radio"
                        name="purchase-studio"
                        value={venue.venue_id}
                        checked={checked}
                        onChange={() => selectVenue(venue.venue_id)}
                        className="mt-0.5 h-5 w-5 shrink-0 appearance-none rounded-full border-2 border-[#7d7568] bg-white checked:border-[6px] checked:border-[#123b34] checked:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#b7892d] focus-visible:ring-offset-2 [color-scheme:light]"
                      />
                      <span>
                        <span className="block text-sm font-bold text-[#123b34]">{getLiubaVenueDisplayName(venue)}</span>
                        <span className="mt-0.5 block text-xs leading-5 text-[#61706b]">{venue.area_label} · {venue.postcode}</span>
                      </span>
                    </label>
                  );
                })}
              </div>
            </fieldset>
          <div>
            <h3 className="font-serif text-2xl font-semibold text-[#123b34]">What is included</h3>
            <ul className="mt-3 space-y-2 text-sm font-semibold leading-6 text-[#53615c]">
              {selectedPurchaseOption.productKey !== "liuba_intro_lesson" ? (
                <>
                  <li>{getLocalLessonProduct(selectedPurchaseOption.productKey).sessionCount} separate {getLocalLessonProduct(selectedPurchaseOption.productKey).sessionCount === 1 ? "session" : "sessions"}</li>
                  <li>{formatSessionDuration(getLocalLessonProduct(selectedPurchaseOption.productKey).sessionDurationMinutes)} per session</li>
                  <li>Total coaching time: {formatTotalCoachingTime(getLocalLessonProduct(selectedPurchaseOption.productKey).totalCoachingMinutes)}</li>
                </>
              ) : null}
              <li>{selectedPurchaseOption.summary}</li>
              <li>{selectedPurchaseOption.detail}</li>
              <li>{selectedPurchaseOption.result}</li>
            </ul>
          </div>
          {getLocalLessonProduct(selectedPurchaseOption.productKey).promoteFlexiblePayments ? (
            <p className="rounded-[14px] border border-[#e2d3bc] bg-[#fffaf2] p-3 text-xs font-semibold leading-5 text-[#61706b]">{CHECKOUT_FLEXIBLE_PAYMENT_DISCLAIMER}</p>
          ) : null}
          <div className="rounded-[18px] border border-[#e2d3bc] bg-white p-4">
            <h3 className="font-bold text-[#123b34]">What happens next</h3>
            {calendarFirstEnabled || isAutomaticFlow ? (
              <ol className="mt-3 space-y-2 text-sm leading-6 text-[#53615c]"><li>1. Add your details.</li><li>2. {calendarFirstEnabled ? "Select and temporarily hold an available time before payment." : "Choose a provisional time from Liuba’s live availability."}</li><li>3. Review the terms and complete secure Stripe payment.</li><li>4. {calendarFirstEnabled ? !selectedVenue ? "Ely appointments are confirmed after successful payment and calendar booking. Bury room confirmation follows manually; the lesson remains provisional until written confirmation." : isBuryVenue ? "Liuba’s selected time is reserved after payment; the room is checked manually. Your lesson remains provisional until written room confirmation." : "Your paid appointment is confirmed once the calendar booking succeeds. Liuba’s Google Calendar is linked to the booking." : "Receive a separate final lesson confirmation."}</li></ol>
            ) : (
              <ol className="mt-3 space-y-2 text-sm leading-6 text-[#53615c]"><li>1. Choose Ely or Bury and add your details.</li><li>2. Add a preferred date and time.</li><li>3. Complete secure Stripe payment.</li><li>4. We check Liuba&apos;s availability and, for Bury, verify the room with The Rehearsal Rooms.</li><li>5. Receive a separate final lesson confirmation.</li></ol>
            )}
            <p className="mt-3 text-sm font-semibold leading-6 text-[#123b34]">{calendarFirstEnabled || isAutomaticFlow
              ? calendarFirstEnabled ? "Selecting a time creates a temporary hold. Payment confirms Liuba’s appointment; Bury room confirmation remains a separate manual step." : "The calendar time is provisional until payment succeeds and you receive the separate final lesson confirmation."
              : LOCAL_LESSON_MANUAL_ORDERING_COPY}</p>
            {isBuryVenue ? <p className="mt-2 text-sm leading-6 text-[#53615c]">{BURY_MANUAL_ORDERING_COPY}</p> : null}
          </div>
          <div className="rounded-[18px] border border-[#d8cab6] bg-[#fffdf8] p-4 text-sm leading-6 text-[#53615c]">
            <p className="font-bold text-[#123b34]">Cancellation and refund policy</p>
            <p className="mt-1">Give at least 48 hours&apos; notice. Later cancellations are treated as used unless Liuba agrees an exception; reschedules are at Liuba&apos;s discretion. Any refund due is returned to the original payment method.</p>
            <Link href="/terms" target="_blank" rel="noreferrer" className="mt-2 inline-flex font-bold text-[#7a5114] underline underline-offset-4">Read the full Terms</Link>
          </div>
          {errorMessage ? <p role="alert" data-calendar-state={calendarRecoveryStatus ?? undefined} className={`rounded-[14px] border p-3 text-sm font-semibold ${LIGHT_SURFACE_ERROR_CLASS}`}>{errorMessage}</p> : null}
        </div>
      ) : purchaseStep === "details" ? (
        <div data-clarity-mask="true" className="grid gap-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="grid gap-2 text-sm font-bold text-[#53615c]"><span>Your name</span><input value={customerName} onChange={(event) => setCustomerName(event.target.value)} autoComplete="name" required aria-invalid={Boolean(errorMessage)} className="min-h-12 rounded-[14px] border border-[#d8cab6] bg-white px-3 text-sm font-semibold text-[#13211f] outline-none focus:border-[#7a5114]" /></label>
            <label className="grid gap-2 text-sm font-bold text-[#53615c]"><span>Email address</span><input value={customerEmail} onChange={(event) => setCustomerEmail(event.target.value)} type="email" inputMode="email" autoComplete="email" maxLength={254} required aria-invalid={Boolean(errorMessage)} className="min-h-12 rounded-[14px] border border-[#d8cab6] bg-white px-3 text-sm font-semibold text-[#13211f] outline-none focus:border-[#7a5114]" /></label>
          </div>
          <label className="grid gap-2 text-sm font-bold text-[#53615c]"><span>Phone</span><div className="grid grid-cols-[7rem_minmax(0,1fr)] gap-2"><select aria-label="Phone country" value={phoneCountry} onChange={(event) => setPhoneCountry(event.target.value)} className="min-h-12 rounded-[14px] border border-[#d8cab6] bg-white px-2 text-sm font-semibold text-[#13211f]"><option value="GB">UK +44</option><option value="IE">IE +353</option><option value="US">US +1</option><option value="CA">CA +1</option><option value="AU">AU +61</option></select><input aria-label="Phone" value={customerPhone} onChange={(event) => setCustomerPhone(event.target.value)} type="tel" inputMode="tel" autoComplete="tel" required aria-invalid={Boolean(errorMessage)} placeholder="Phone number" className="min-h-12 min-w-0 rounded-[14px] border border-[#d8cab6] bg-white px-3 text-sm font-semibold text-[#13211f] outline-none focus:border-[#7a5114]" /></div></label>
          <p className="text-xs font-semibold leading-5 text-[#61706b]">{isAutomaticFlow
            ? "These details are used to load live availability and prepare secure payment. No portal account is required."
            : "These details are used for payment and manual scheduling. No portal account is required."}</p>
          {errorMessage ? <p role="alert" data-calendar-state={calendarRecoveryStatus ?? undefined} className={`rounded-[14px] border p-3 text-sm font-semibold ${LIGHT_SURFACE_ERROR_CLASS}`}>{errorMessage}</p> : null}
        </div>
      ) : purchaseStep === "calendar" && calendarFirstEnabled && selectedVenue ? (
        <LocalLessonTimeHoldStep key={`${selectedPurchaseOption.productKey}:${selectedVenue.venue_id}`}
          productKey={selectedPurchaseOption.productKey} venueId={selectedVenue.venue_id}
          customer={{ name: customerName.trim(), email: normalizeLocalLessonEmail(customerEmail) ?? "", phone: parsePhoneNumberFromString(customerPhone.trim(), phoneCountry as "GB")?.number ?? customerPhone }} sourcePage={sourcePage} sourceCta={selectedPurchaseOption.buttonLabel}
          isPack={getLocalLessonProduct(selectedPurchaseOption.productKey).sessionCount > 1}
          onHeld={(hold) => { setTimeHold(hold); setHoldSeconds(Math.max(0, Math.floor((getCheckoutStartDeadline(hold) - Date.now()) / 1000))); setPurchaseStep("consent"); setConsentError(null); }} />
      ) : purchaseStep === "calendar" && calendarLink ? (
        <div data-testid="local-lesson-calendar-state" data-calendar-state={calendarRecoveryStatus === "expired" ? "expired" : "available"} className="space-y-4">
          <div className="rounded-[16px] border border-[#d9c78f]/65 bg-[#fff5df] p-4 text-sm leading-6 text-[#53615c]">
            <p className="font-bold text-[#123b34]">{selectedVenue ? getLiubaVenueDisplayName(selectedVenue) : "Selected studio"}</p>
            <p>Choose a provisional time from Liuba&apos;s live availability. Payment settlement, appointment acceptance and the separate final confirmation remain distinct steps.</p>
          </div>
          {calendarRecoveryStatus === "expired" ? (
            <p role="alert" className={`rounded-[14px] border p-3 text-sm font-semibold ${LIGHT_SURFACE_ERROR_CLASS}`}>
              That provisional time is no longer available. Please choose another live time.
            </p>
          ) : null}
          <LocalLessonCalStep
            calendarLink={calendarLink}
            customerName={customerName.trim()}
            customerEmail={customerEmail.trim()}
            productKey={selectedPurchaseOption.productKey}
            locationSlug={selectedVenue?.venue_slug ?? "not_selected"}
            onBookingSelected={(uid) => { setSelectedCalBookingUid(uid); setCalendarRecoveryStatus(null); setPurchaseStep("consent"); }}
            onLoadError={activateManualFallback}
          />
        </div>
      ) : purchaseStep === "preferences" ? (
        <div data-testid="local-lesson-calendar-state" data-calendar-state={calendarRecoveryStatus ?? undefined} className="space-y-4">
          {manualFallbackNotice ? <p role="status" className="rounded-[16px] border border-[#d9c78f]/65 bg-[#fff5df] p-4 text-sm font-semibold leading-6 text-[#53615c]">{manualFallbackNotice}</p> : null}
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="grid gap-2 text-sm font-bold text-[#53615c]"><span>Preferred date</span><input type="date" value={requestedDate} onChange={(event) => setRequestedDate(event.target.value)} required className="min-h-12 rounded-[14px] border border-[#d8cab6] bg-white px-3 text-sm font-semibold text-[#13211f]" /></label>
            <label className="grid gap-2 text-sm font-bold text-[#53615c]"><span>Preferred time</span><input type="time" value={requestedTime} onChange={(event) => setRequestedTime(event.target.value)} required className="min-h-12 rounded-[14px] border border-[#d8cab6] bg-white px-3 text-sm font-semibold text-[#13211f]" /></label>
          </div>
          <label className="grid gap-2 text-sm font-bold text-[#53615c]"><span>Alternative times (optional)</span><textarea value={requestedAlternatives} onChange={(event) => setRequestedAlternatives(event.target.value)} maxLength={240} rows={3} placeholder="For example, weekday mornings or Thursday after 5pm" className="rounded-[14px] border border-[#d8cab6] bg-white p-3 text-sm font-semibold text-[#13211f]" /></label>
          <div className="rounded-[16px] border border-[#d9c78f]/65 bg-[#fff5df] p-4 text-sm leading-6 text-[#53615c]"><p>{LOCAL_LESSON_MANUAL_ORDERING_COPY}</p>{isBuryVenue ? <p className="mt-2">{BURY_MANUAL_ORDERING_COPY}</p> : null}</div>
          {errorMessage ? <p role="alert" data-calendar-state={calendarRecoveryStatus ?? undefined} className={`rounded-[14px] border p-3 text-sm font-semibold ${LIGHT_SURFACE_ERROR_CLASS}`}>{errorMessage}</p> : null}
        </div>
      ) : purchaseStep === "consent" ? (
        <div className="space-y-4">
        {calendarFirstEnabled && timeHold ? <div role="status" className="rounded-[14px] border border-[#d9c78f] bg-[#fff5df] p-4 text-sm leading-6 text-[#53615c]">
          <p className="font-bold text-[#123b34]">{formatLocalLessonTime(timeHold.selectedStart)} · UK time</p>
          <p>{holdSeconds > 0 ? `Start secure payment within ${Math.floor(holdSeconds / 60)}:${String(holdSeconds % 60).padStart(2, "0")}. Once checkout opens, Stripe shows the payment deadline.` : "The payment-start window has ended. Choose another available time before payment."}</p>
          {isBuryVenue ? <p>Room confirmation follows manually after payment. Your lesson remains provisional until written confirmation.</p> : <p>Payment confirms the appointment once the calendar booking succeeds.</p>}
        </div> : null}
        <CheckoutConsentStep
          requiresElyEarlyPerformanceRequest={calendarFirstEnabled && selectedVenue?.venue_id === "ely_studio"}
          value={checkoutConsent}
          onChange={(next) => { setCheckoutConsent(next); setConsentError(null); }}
          validationError={consentError}
        />
        </div>
      ) : null : null}
    </PurchaseReviewDialog>
    {vivianaVideoModal}
    </>
  );
}
